import React, { useMemo } from 'react';
import { BaselineData, TopologyData, getLatencyColor } from '../TopologyData';
import {
    LatencyCell,
    LatencyPathMetric,
    LatencyRoute,
    computeLatencyMatrix,
    formatDelayMs,
    worstRouteVsBaseline,
} from '../utils/latencyMatrix';
import RouterMatrixGrid, { matrixRouterLabel } from './RouterMatrixGrid';
import LatencyBaselineLegend from './LatencyBaselineLegend';

interface Props {
    data: TopologyData;
    baseline: BaselineData | null;
    metric: LatencyPathMetric;
}

const METRIC_LABEL: Record<LatencyPathMetric, string> = {
    igp: 'IGP cost (DEFAULT)',
    'min-delay': 'min delay (LATENCY)',
};

/** The cell text: one value, or the equal-cost spread when it is visible. */
function cellText(cell: LatencyCell): string {
    if (cell.minUs == null) return '—';
    const min = formatDelayMs(cell.minUs);
    if (cell.maxUs == null) return `${min}+`;
    const max = formatDelayMs(cell.maxUs);
    return min === max ? min : `${min}–${max}`;
}

/** Spread is only worth flagging once it survives the cell's rounding. */
function hasVisibleSpread(cell: LatencyCell): boolean {
    if (cell.minUs == null) return false;
    if (cell.maxUs == null) return true;
    return formatDelayMs(cell.minUs) !== formatDelayMs(cell.maxUs);
}

function routeLine(route: LatencyRoute): string {
    const delay = route.delayUs == null ? 'n/a' : `${formatDelayMs(route.delayUs)} ms`;
    const path = [
        matrixRouterLabel(route.hops[0]?.from ?? ''),
        ...route.hops.map(
            (hop) => `${matrixRouterLabel(hop.to)}${hop.borrowed ? '*' : ''}`
        ),
    ].join(' › ');
    return `${delay}  ${path}`;
}

function describeCell(
    row: string,
    col: string,
    cell: LatencyCell,
    metric: LatencyPathMetric
): string {
    const cost =
        metric === 'igp'
            ? `IGP cost ${cell.cost}`
            : `min-delay cost ${formatDelayMs(cell.cost)} ms`;
    const paths =
        cell.routes.length === 1
            ? '1 path'
            : `${cell.routes.length}${cell.truncated ? '+' : ''} equal-cost paths`;

    const lines = [
        `${matrixRouterLabel(row)} → ${matrixRouterLabel(col)}`,
        `${cost} · ${paths}`,
        ...cell.routes.map(routeLine),
    ];
    if (cell.hasBorrowedHop) {
        lines.push('* this direction is not measured; the opposite one was used');
    }
    return lines.join('\n');
}

function cellColor(cell: LatencyCell): string | undefined {
    const worst = worstRouteVsBaseline(cell);
    if (!worst?.delayUs || !worst.baselineUs) return undefined;
    return getLatencyColor(worst.delayUs, worst.baselineUs);
}

const LatencyMatrix: React.FC<Props> = ({ data, baseline, metric }) => {
    const { routers, cells } = useMemo(
        () => computeLatencyMatrix(data, metric, baseline),
        [data, metric, baseline]
    );

    const renderCell = (row: string, col: string) => {
        const cell = cells[row]?.[col];
        if (!cell) {
            return (
                <td
                    className="p-1 border border-semantic-border-secondary"
                    title={`${matrixRouterLabel(col)} is unreachable from ${matrixRouterLabel(row)} with ${METRIC_LABEL[metric]}`}
                />
            );
        }
        const spread = hasVisibleSpread(cell);
        return (
            <td
                className="p-1 border border-semantic-border-secondary
                    text-center font-semibold whitespace-nowrap"
                style={{
                    color: cellColor(cell),
                    borderStyle: spread ? 'dashed' : undefined,
                    borderColor: spread
                        ? 'var(--color-semantic-text-secondary)'
                        : undefined,
                }}
                title={describeCell(row, col, cell, metric)}
            >
                {/* Borrowed directions are flagged only in the tooltip: the
                    QRO01 ↔ QRO02 links sit on ~1 in 4 paths and differ by
                    tens of µs, so an in-cell mark would be mostly noise. */}
                {cellText(cell)}
            </td>
        );
    };

    return (
        <div className="flex flex-col gap-3 h-full overflow-auto p-4">
            <p className="text-xs text-semantic-text-secondary">
                One-way delay in ms from the row router to the column router,
                summed link by link along the {METRIC_LABEL[metric]} path.
                Hover a cell for its path.
            </p>

            <RouterMatrixGrid
                ids={routers}
                renderCell={renderCell}
                minCellWidth={44}
            />

            <div className="flex items-center gap-4 flex-wrap text-xs text-semantic-text-secondary mt-1">
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block w-3 h-3 border rounded-sm"
                        style={{
                            borderStyle: 'dashed',
                            borderColor: 'var(--color-semantic-text-secondary)',
                        }}
                    />
                    Equal-cost paths with different delay (fastest–slowest)
                </span>
                <LatencyBaselineLegend />
            </div>
        </div>
    );
};

export default LatencyMatrix;
